import DOMPurify from 'dompurify';

// Aikido SAST (EWM-700): sanitize `html` with DOMPurify before assigning to innerHTML.
// The function returns plain text (textContent), so sanitization does not change the
// output for legitimate input — it only prevents script injection through the sink.
//
// EWM-1135: memoized, because this is called from render paths rather than from event handlers.
// The Management Report runs it once per assumption per row inside the assumptions cell, so a
// single keystroke in the search box re-sanitizes every visible assumption. Profiled against TEST
// on 2026-09-23: DOMPurify was the largest single cost on the tab at ~1.0 s per keystroke, on an
// estimation of only 102 items — the sanitizing, not the row count, was the bottleneck.
//
// Safe to cache: the function is pure for a given input, and sanitization still happens on the
// first call for every distinct string. Nothing reaches innerHTML unsanitized.
//
// Eviction is a whole-cache clear rather than an LRU. Descriptions repeat heavily across rows and
// renders, which is the case worth optimising; the ceiling only exists so a long session cannot
// grow the map without bound. If a workload ever thrashes it, an LRU is the upgrade.
const STRIP_HTML_CACHE_LIMIT = 1000;
const stripHtmlCache = new Map<string, string>();

export const stripHtml = (html: string) => {
  const cached = stripHtmlCache.get(html);
  if (cached !== undefined) {
    return cached;
  }

  const tmp = document.createElement('DIV');
  tmp.innerHTML = DOMPurify.sanitize(html);
  const text = tmp.textContent || tmp.innerText || '';

  if (stripHtmlCache.size >= STRIP_HTML_CACHE_LIMIT) {
    stripHtmlCache.clear();
  }
  stripHtmlCache.set(html, text);

  return text;
};

export const toKebabCase = (str: string) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};
